import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import Footer from "../components/Footer";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { JOIN_FORM_URL } from "../config/links";

const roots = [
  {
    name: "OpenStreetMap and HOT",
    href: "https://www.hotosm.org/en/",
    description:
      "Years of work in open and humanitarian mapping, including FAIR tooling, HOT's technology products, and the wider OpenStreetMap community.",
  },
  {
    name: "YouthMappers",
    href: "https://www.youthmappers.org/",
    description:
      "Research, mentorship, and community work within a global network of student mappers across more than 70 countries.",
  },
  {
    name: "MapMakoko",
    href: "https://www.hotosm.org/en/news/mapping-makoko-using-drones-and-canoes/",
    description:
      "Community mapping in Makoko, Lagos, where residents built a record of places that official systems had left out.",
  },
  {
    name: "Nigerian Energy Support Programme",
    href: "https://nigeriase4all.gov.ng/",
    description:
      "National-scale electrification planning supported by GIZ and the European Union, using data infrastructure to inform access for millions of households.",
  },
  {
    name: "eHealth Africa",
    href: "https://ehealthafrica.org/",
    description:
      "Digital health, GIS, and emergency-response infrastructure across Nigeria and West Africa, including lessons from the Ebola response.",
  },
  {
    name: "Kubernetes",
    href: "https://kubernetes.io/",
    description:
      "Open-source contribution at global scale, with the upstream reviews, working groups, and maintenance discipline that serious public infrastructure requires.",
  },
  {
    name: "Posit",
    href: "https://posit.co/",
    description:
      "Recognition through the Posit Diversity Scholarship and a long-standing connection to open data-science tooling.",
  },
];

const practices = [
  {
    title: "We ship in the open",
    description:
      "Projects begin in public repositories. Issues, design decisions, documentation, and tradeoffs remain visible.",
  },
  {
    title: "We license for reuse",
    description:
      "Code, datasets, and models use clear terms that allow institutions and communities to adopt the work on their own terms.",
  },
  {
    title: "We pay contributors",
    description:
      "When work is funded, contributors are paid fairly. Community is not a euphemism for extracting unpaid labour.",
  },
  {
    title: "We document for the next person",
    description:
      "Each project has a clear onramp, contribution guide, and enough context for someone arriving a year later.",
  },
];

const organizationModel = [
  {
    title: "Research",
    description:
      "We study hard technical questions in representation, identity, cryptography, AI, agents, world models, and public infrastructure.",
  },
  {
    title: "Labs",
    description:
      "We turn the strongest questions into open-source tools, applied pilots, and reusable technical infrastructure.",
  },
  {
    title: "Knowledge network",
    description:
      "We convene engineers, researchers, designers, creatives, operators, robotics builders, and students through writing, seminars, education, and annual gatherings.",
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Unpatterned | Research, infrastructure, and knowledge network"
        description="Unpatterned is an independent research, infrastructure, and knowledge network building open systems for data, identity, and intelligence."
      />
      <Header />

      <main>
        <section className="px-6 pb-20 pt-32 md:pb-28 md:pt-40">
          <div className="mx-auto max-w-6xl">
            <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
              About Unpatterned
            </p>
            <h1 className="mt-8 max-w-5xl font-['DM_Serif_Display',serif] text-5xl leading-[1.02] text-[#050e1d] sm:text-6xl md:text-7xl">
              An institute, a lab, and a network for serious technology work.
            </h1>

            <div className="mt-12 grid gap-8 border-t border-[#050e1d]/15 pt-8 md:grid-cols-12">
              <p className="font-['DM_Serif_Display',serif] text-2xl leading-snug text-[#050e1d] md:col-span-5 md:text-3xl">
                Unpatterned exists to help emerging markets build and govern
                the technologies that will shape the next generation.
              </p>
              <p className="font-['Manrope',sans-serif] text-lg leading-relaxed text-[#050e1d]/65 md:col-span-6 md:col-start-7 md:text-xl">
                We work across research, open-source tools, applied pilots,
                education, and convening. Our first focus is representation
                infrastructure: systems that help records refer to real people,
                places, products, organizations, and knowledge.
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-[#050e1d]/10 px-6 py-20 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                  The model
                </p>
                <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                  Build the work, then build the field around it.
                </h2>
              </div>
              <p className="font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60 md:col-span-5 md:col-start-8 md:self-end">
                The goal is not only to publish papers or ship tools. It is to
                create a place where rigorous research, working software,
                education, design, and convening reinforce each other.
              </p>
            </div>

            <div className="mt-12 grid border-t border-[#050e1d]/15 md:grid-cols-3">
              {organizationModel.map((item, index) => (
                <article
                  key={item.title}
                  className={`border-b border-[#050e1d]/15 py-8 md:px-7 md:py-10 ${
                    index > 0 ? "md:border-l" : ""
                  }`}
                >
                  <span className="font-mono text-xs text-[#184b9d]/55">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-5 font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d]">
                    {item.title}
                  </h3>
                  <p className="mt-3 font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/60">
                    {item.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 md:py-28">
          <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-4">
              <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                Why we exist
              </p>
              <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                The gap was visible from the work.
              </h2>
            </div>

            <div className="space-y-6 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/70 md:col-span-7 md:col-start-6 md:text-lg">
              <p>
                Our team has worked across humanitarian mapping in Lagos,
                electrification planning in Nigeria, health-system response in
                West Africa, global technology companies, and open-source
                projects from Kubernetes to OpenStreetMap.
              </p>
              <p>
                The same problem kept appearing: institutions needed durable
                technical capacity, but the tools, knowledge, and networks were
                fragmented, closed, or built far from the people and conditions
                they were meant to serve.
              </p>
              <p>
                Reports alone do not close that gap. Working software, credible
                engineering, open standards, education, and a strong network of
                serious technical and creative builders can.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#f7f8fa] px-6 py-20 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                  Our roots
                </p>
                <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                  Accountable to the communities that shaped the work.
                </h2>
              </div>
              <p className="font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60 md:col-span-5 md:col-start-8 md:self-end">
                These are not logos for a credibility wall. They are places
                where we contributed, learned, and formed the practices we use
                today.
              </p>
            </div>

            <div className="mt-14 border-t border-[#050e1d]/15">
              {roots.map((root, index) => (
                <article
                  key={root.name}
                  className="grid gap-4 border-b border-[#050e1d]/15 py-7 sm:grid-cols-[56px_1fr] md:grid-cols-[72px_280px_1fr] md:gap-8 md:py-8"
                >
                  <span className="pt-1 font-mono text-xs text-[#184b9d]/55">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>
                    <a
                      href={root.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-['Manrope',sans-serif] text-base font-semibold text-[#050e1d] transition-colors hover:text-[#184b9d]"
                    >
                      {root.name}
                    </a>
                  </h3>
                  <p className="font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/60 md:text-base">
                    {root.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-20 md:py-28">
          <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-4">
              <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                Practices
              </p>
              <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                How we make the work durable.
              </h2>
            </div>

            <div className="border-t border-[#050e1d]/15 md:col-span-7 md:col-start-6">
              {practices.map((practice, index) => (
                <article
                  key={practice.title}
                  className="grid gap-3 border-b border-[#050e1d]/15 py-7 sm:grid-cols-[44px_1fr]"
                >
                  <span className="pt-1 font-mono text-xs text-[#184b9d]/55">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d]">
                      {practice.title}
                    </h3>
                    <p className="mt-2 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                      {practice.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f2f6fc] px-6 py-20 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-12">
            <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:col-span-7 md:text-5xl">
              The work is public. The invitation is open.
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row md:col-span-5 md:self-end">
              <Link
                to="/labs"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#050e1d] px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-white transition-colors hover:bg-[#184b9d]"
              >
                Explore the labs
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <a
                href={JOIN_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md border border-[#050e1d]/20 px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-[#050e1d] transition-colors hover:border-[#050e1d]"
              >
                Join us
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
